/**
 * <truncate-content> — Tronque un contenu sur N lignes via CSS line-clamp,
 * et affiche un bouton "Voir plus / Voir moins" si le contenu déborde.
 *
 * Structure attendue (cf. snippets/truncate.liquid) :
 *
 *   <truncate-content
 *     style="--truncate-lines: 3"
 *     data-expanded="false"
 *     data-more-label="Voir plus"
 *     data-less-label="Voir moins"
 *   >
 *     <div class="truncate__content">…contenu HTML…</div>
 *     <button type="button" class="truncate__toggle" aria-expanded="false" hidden>
 *       Voir plus
 *     </button>
 *   </truncate-content>
 *
 * Le bouton reste [hidden] tant que le contenu ne déborde pas. La détection
 * est faite au connect, au resize du contenu (ResizeObserver) et après
 * chargement des polices web (qui changent la hauteur de ligne).
 */
(function () {
  if (customElements.get("truncate-content")) return;

  class TruncateComponent extends HTMLElement {
    connectedCallback() {
      this.content = this.querySelector(":scope > .truncate__content");
      this.toggle = this.querySelector(":scope > .truncate__toggle");
      if (!this.content || !this.toggle) return;

      this.moreLabel = this.dataset.moreLabel || "Voir plus";
      this.lessLabel = this.dataset.lessLabel || "Voir moins";

      // Lie le bouton au contenu pour les lecteurs d'écran.
      if (!this.content.id) {
        this.content.id = `truncate-${Math.random().toString(36).slice(2, 9)}`;
      }
      this.toggle.setAttribute("aria-controls", this.content.id);
      this.toggle.addEventListener("click", this.#onClick);

      this.#checkOverflow();
      this.resizeObserver = new ResizeObserver(() => this.#checkOverflow());
      this.resizeObserver.observe(this.content);
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => this.#checkOverflow());
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.toggle?.removeEventListener("click", this.#onClick);
    }

    /**
     * Mesure le débordement uniquement en état "tronqué" : quand le contenu
     * est déplié, scrollHeight == clientHeight, on ne peut rien déduire. On
     * re-mesurera au prochain repli.
     */
    #checkOverflow() {
      if (!this.content || !this.toggle) return;
      if (this.dataset.expanded === "true") return;
      const overflows = this.content.scrollHeight > this.content.clientHeight + 1;
      this.toggle.hidden = !overflows;
    }

    #setExpanded(expanded) {
      this.dataset.expanded = expanded ? "true" : "false";
      this.toggle.setAttribute("aria-expanded", expanded ? "true" : "false");
      this.toggle.textContent = expanded ? this.lessLabel : this.moreLabel;
      if (!expanded) this.#checkOverflow();
    }

    #onClick = () => {
      this.#setExpanded(this.dataset.expanded !== "true");
    };
  }

  customElements.define("truncate-content", TruncateComponent);
})();
